// Scheduled holiday themes (docs/holiday-themes.md). Pure rules only: which holidays exist, which ones
// the app can draw, and how a scheduled window replaces a person's color theme.
import { appearanceBaseTheme, type AppearanceTone } from '../appearance';

export type HolidayThemeKey = 'halloween' | 'thanksgiving' | 'christmas' | 'newyear' | 'stpatricks' | 'easter' | 'july4';
export type HolidayTone = 'light' | 'dark';

export type HolidayWindow = { id: string; themeKey: HolidayThemeKey; startsAt: string; endsAt: string };
export type HolidayThemeState = { active: HolidayWindow | null; next: HolidayWindow | null; serverNow: string };

type HolidayThemeMeta = { key: HolidayThemeKey; label: string; available: boolean };

// The database accepts all seven keys; only holidays with finished art and palettes are offered or shown.
export const HOLIDAY_THEMES: readonly HolidayThemeMeta[] = [
  { key: 'halloween', label: 'Halloween', available: true },
  { key: 'thanksgiving', label: 'Thanksgiving', available: true },
  { key: 'christmas', label: 'Christmas', available: true },
  { key: 'newyear', label: "New Year's", available: true },
  { key: 'stpatricks', label: "St. Patrick's Day", available: true },
  { key: 'easter', label: 'Easter', available: true },
  { key: 'july4', label: 'Fourth of July', available: true },
];

export const AVAILABLE_HOLIDAY_THEMES = HOLIDAY_THEMES.filter((theme) => theme.available);

/** Admin times are entered and shown in Eastern time. */
export const HOLIDAY_THEME_TIME_ZONE = 'America/New_York';

/** Light holidays sit on Green and dark ones on Dark, so no other theme's special backgrounds show through. */
export const HOLIDAY_BASE_THEME: Record<HolidayTone, 'green' | 'dark'> = { light: 'green', dark: 'dark' };

export function isHolidayThemeKey(value: unknown): value is HolidayThemeKey {
  return typeof value === 'string' && HOLIDAY_THEMES.some((theme) => theme.key === value);
}

export function isAvailableHolidayTheme(value: unknown): value is HolidayThemeKey {
  return typeof value === 'string' && AVAILABLE_HOLIDAY_THEMES.some((theme) => theme.key === value);
}

export function holidayThemeLabel(key: HolidayThemeKey): string {
  return HOLIDAY_THEMES.find((theme) => theme.key === key)?.label ?? key;
}

/** Dark and Neural Ink are the dark color themes; everyone else gets a holiday's light tone. */
export function holidayToneFor(colorTheme: string | null | undefined): HolidayTone {
  return colorTheme === 'dark' || colorTheme === 'neural-ink' ? 'dark' : 'light';
}

function parseWindow(value: unknown): HolidayWindow | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const id = typeof row.id === 'string' ? row.id : null;
  const themeKey = row.theme_key ?? row.themeKey;
  const startsAt = row.starts_at ?? row.startsAt;
  const endsAt = row.ends_at ?? row.endsAt;
  if (!id || !isHolidayThemeKey(themeKey) || typeof startsAt !== 'string' || typeof endsAt !== 'string') return null;
  if (!Number.isFinite(Date.parse(startsAt)) || !(Date.parse(endsAt) > Date.parse(startsAt))) return null;
  return { id, themeKey, startsAt, endsAt };
}

export function parseHolidayWindow(value: unknown): HolidayWindow | null {
  return parseWindow(value);
}

/** Parse `holiday_theme_current()`; anything malformed reads as "no holiday". */
export function parseHolidayThemeState(value: unknown): HolidayThemeState | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const serverNow = typeof row.server_now === 'string' && Number.isFinite(Date.parse(row.server_now)) ? row.server_now : null;
  if (!serverNow) return null;
  return { active: parseWindow(row.active), next: parseWindow(row.next), serverNow };
}

export function holidayWindowOpenAt(window: HolidayWindow | null, nowMs: number): boolean {
  return Boolean(window && Date.parse(window.startsAt) <= nowMs && nowMs < Date.parse(window.endsAt));
}

/** The window open at `nowMs`: the active one until it ends, then the next one once it starts. */
export function openHolidayWindow(state: HolidayThemeState | null, nowMs: number): HolidayWindow | null {
  if (!state) return null;
  if (holidayWindowOpenAt(state.active, nowMs)) return state.active;
  if (holidayWindowOpenAt(state.next, nowMs)) return state.next;
  return null;
}

/** The next moment the open window changes, or null when nothing more is known. */
export function nextHolidayBoundary(state: HolidayThemeState | null, nowMs: number): number | null {
  if (!state) return null;
  const times = [state.active?.endsAt, state.next?.startsAt, state.next?.endsAt]
    .map((value) => (value ? Date.parse(value) : Number.NaN))
    .filter((value) => Number.isFinite(value) && value > nowMs);
  return times.length ? Math.min(...times) : null;
}

/** "Oct 31" for a window ending at midnight Eastern on Nov 1, else "Oct 31, 6:00 PM". */
export function holidayWindowEndLabel(endsAt: string): string {
  const end = Date.parse(endsAt);
  if (!Number.isFinite(end)) return '';
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: HOLIDAY_THEME_TIME_ZONE, hour: 'numeric', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(new Date(end));
  const midnight = parts.find((part) => part.type === 'hour')?.value === '00' && parts.find((part) => part.type === 'minute')?.value === '00';
  const day = new Intl.DateTimeFormat('en-US', { timeZone: HOLIDAY_THEME_TIME_ZONE, month: 'short', day: 'numeric' });
  if (midnight) return day.format(new Date(end - 1));
  const time = new Intl.DateTimeFormat('en-US', { timeZone: HOLIDAY_THEME_TIME_ZONE, hour: 'numeric', minute: '2-digit' });
  return `${day.format(new Date(end))}, ${time.format(new Date(end))}`;
}

export type ResolvedShellTheme ={ dataTheme: string; holiday: HolidayThemeKey | null; tone: HolidayTone | null };

/**
 * The theme a screen shows. A scheduled holiday replaces the person's color theme unless they opted out
 * of that window (it never carries to the next one); a session preview wins over both. Saved preferences
 * never change.
 */
export function resolveShellTheme(params: {
  colorTheme: string;
  scheduled: HolidayWindow | null;
  optOutId: string | null;
  preview: HolidayThemeKey | null;
  appearanceTone?: AppearanceTone;
}): ResolvedShellTheme {
  const key = params.preview
    ?? (params.scheduled && params.scheduled.id !== params.optOutId ? params.scheduled.themeKey : null);
  const tone = params.appearanceTone ?? holidayToneFor(params.colorTheme);
  if (!key || !isAvailableHolidayTheme(key)) return {
    dataTheme: params.appearanceTone ? appearanceBaseTheme(params.colorTheme, tone) : params.colorTheme,
    holiday: null,
    tone: null,
  };
  return { dataTheme: HOLIDAY_BASE_THEME[tone], holiday: key, tone };
}

// ---- Usual dates, suggestions and labels (Settings › Holiday themes) ----

/** How each holiday's window usually runs, in Eastern dates (the canvas's ranges; migration 20261019205500). */
const USUAL_DATES: Record<HolidayThemeKey, string> = {
  halloween: 'Oct 24 – 31',
  thanksgiving: 'Monday to Friday of Thanksgiving week',
  christmas: 'Dec 1 – 26',
  newyear: 'Dec 30 – Jan 2',
  stpatricks: 'Mar 14 – 17',
  easter: 'the Thursday before Easter through Easter Monday',
  july4: 'Jul 1 – 4',
};

export function holidayUsualDates(key: HolidayThemeKey): string {
  return USUAL_DATES[key];
}

export type HolidayWindowTimes = { startsAt: string; endsAt: string };

const DAY_MS = 86_400_000;
type WallDate = { year: number; month: number; day: number };

const easternWall = new Intl.DateTimeFormat('en-CA', {
  timeZone: HOLIDAY_THEME_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
const easternDay = new Intl.DateTimeFormat('en-US', { timeZone: HOLIDAY_THEME_TIME_ZONE, weekday: 'short', month: 'short', day: 'numeric' });
const easternYear = new Intl.DateTimeFormat('en-US', { timeZone: HOLIDAY_THEME_TIME_ZONE, year: 'numeric' });
const easternTime = new Intl.DateTimeFormat('en-US', { timeZone: HOLIDAY_THEME_TIME_ZONE, hour: 'numeric', minute: '2-digit' });

/** The Eastern wall clock at `ms`: its calendar day (as a day count) and whether it is midnight. */
function easternClock(ms: number): { dayIndex: number; midnight: boolean } {
  const parts = Object.fromEntries(easternWall.formatToParts(new Date(ms)).map((part) => [part.type, part.value]));
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return {
    dayIndex: Math.round(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)) / DAY_MS),
    midnight: hour === '00' && parts.minute === '00',
  };
}

function addDays(date: WallDate, days: number): WallDate {
  const moved = new Date(Date.UTC(date.year, date.month - 1, date.day) + days * DAY_MS);
  return { year: moved.getUTCFullYear(), month: moved.getUTCMonth() + 1, day: moved.getUTCDate() };
}

/** Midnight Eastern at the start of `date`. Eastern time is UTC-5 or UTC-4, and midnight is never skipped. */
function easternMidnight(date: WallDate): string {
  const target = Math.round(Date.UTC(date.year, date.month - 1, date.day) / DAY_MS);
  for (const offsetHours of [5, 4]) {
    const instant = Date.UTC(date.year, date.month - 1, date.day, offsetHours);
    const clock = easternClock(instant);
    if (clock.midnight && clock.dayIndex === target) return new Date(instant).toISOString();
  }
  throw new Error(`No Eastern midnight on ${date.year}-${date.month}-${date.day}.`);
}

/** Easter Sunday (the anonymous Gregorian computus). */
function easterSunday(year: number): WallDate {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  return { year, month, day: ((h + l - 7 * m + 114) % 31) + 1 };
}

/** Thanksgiving: the fourth Thursday of November. */
function thanksgivingDay(year: number): WallDate {
  const firstWeekday = new Date(Date.UTC(year, 10, 1)).getUTCDay();
  return { year, month: 11, day: 1 + ((4 - firstWeekday + 7) % 7) + 21 };
}

/**
 * A holiday's usual window in `year`, in Eastern time: midnight at the start of its first day to midnight after
 * its last. New Year's runs into the next year.
 */
export function holidayOccurrence(key: HolidayThemeKey, year: number): HolidayWindowTimes {
  const span = (first: WallDate, after: WallDate) => ({ startsAt: easternMidnight(first), endsAt: easternMidnight(after) });
  switch (key) {
    case 'halloween': return span({ year, month: 10, day: 24 }, { year, month: 11, day: 1 });
    case 'thanksgiving': { const day = thanksgivingDay(year); return span(addDays(day, -3), addDays(day, 2)); }
    case 'christmas': return span({ year, month: 12, day: 1 }, { year, month: 12, day: 27 });
    case 'newyear': return span({ year, month: 12, day: 30 }, { year: year + 1, month: 1, day: 3 });
    case 'stpatricks': return span({ year, month: 3, day: 14 }, { year, month: 3, day: 18 });
    case 'easter': { const sunday = easterSunday(year); return span(addDays(sunday, -3), addDays(sunday, 2)); }
    case 'july4': return span({ year, month: 7, day: 1 }, { year, month: 7, day: 5 });
    default: throw new Error(`Unknown holiday ${String(key)}.`);
  }
}

/**
 * The holiday's first usual window that has not ended by `after` and overlaps none of `taken` (windows may touch),
 * looking up to ten years ahead; null if every one is taken.
 */
export function suggestHolidayWindow(
  key: HolidayThemeKey,
  { after, taken }: { after: number; taken: ReadonlyArray<HolidayWindowTimes> },
): HolidayWindowTimes | null {
  const year = new Date(after).getUTCFullYear();
  for (let offset = -1; offset <= 10; offset += 1) {
    const window = holidayOccurrence(key, year + offset);
    const start = Date.parse(window.startsAt);
    const end = Date.parse(window.endsAt);
    if (end <= after) continue;
    if (taken.some((row) => start < Date.parse(row.endsAt) && end > Date.parse(row.startsAt))) continue;
    return window;
  }
  return null;
}

/** The window's first and last days, Eastern; the last day is the one before a midnight end. */
function windowDays(window: HolidayWindowTimes, now: number): { first: string; last: string; length: number } {
  const start = Date.parse(window.startsAt);
  const end = Date.parse(window.endsAt);
  const endClock = easternClock(end);
  const lastMs = endClock.midnight ? end - 1 : end;
  const lastDate = new Date(lastMs);
  const year = easternYear.format(lastDate) === easternYear.format(new Date(now)) ? '' : `, ${easternYear.format(lastDate)}`;
  const time = endClock.midnight ? '' : `, ${easternTime.format(lastDate)}`;
  return {
    first: easternDay.format(new Date(start)),
    last: `${easternDay.format(lastDate)}${year}${time}`,
    length: easternClock(lastMs).dayIndex - easternClock(start).dayIndex + 1,
  };
}

/** "Sat, Oct 24 – Sat, Oct 31": a window's first and last days, with the year when it is not this year. */
export function holidayWindowDays(window: HolidayWindowTimes, now = Date.now()): string {
  const days = windowDays(window, now);
  return `${days.first} – ${days.last}`;
}

/** "On for 5 days, Mon, Nov 23 through Fri, Nov 27." */
export function holidayWindowSpan(window: HolidayWindowTimes, now = Date.now()): string {
  const days = windowDays(window, now);
  return days.length <= 1 ? `On for 1 day, ${days.first}.` : `On for ${days.length} days, ${days.first} through ${days.last}.`;
}

/** "Starts in 3 days", "Starts tomorrow", "Ends today"…, counted in Eastern calendar days. */
export function holidayCountdown(window: HolidayWindowTimes, now = Date.now()): string {
  const start = Date.parse(window.startsAt);
  const end = Date.parse(window.endsAt);
  const today = easternClock(now).dayIndex;
  const count = (verb: string, ms: number) => {
    const days = easternClock(ms).dayIndex - today;
    return days <= 0 ? `${verb} today` : days === 1 ? `${verb} tomorrow` : `${verb} in ${days} days`;
  };
  if (now < start) return count('Starts', start);
  if (now < end) return count('Ends', end);
  return 'Ended';
}
